import { useOneTap } from "@au-schein-de/sso-client/one-tap";
import { useEffect, useState } from "react";

export type DeferredOneTapProps = {
	clientId: string;
	ssoUrl: string;
	ssoSession: Parameters<typeof useOneTap>[0]["ssoSession"];
};

/**
 * Mounts Google One Tap (`useOneTap`) only after the browser is idle so the
 * GSI client script and the stylesheet it injects don't fire during the
 * cold-load critical path. When the script's stylesheet lands during initial
 * render the browser re-styles the document and re-decodes images against
 * the new compositor layer, which reads as a "blink" on the PDP after first
 * load. Deferring the mount pushes that work past the first stable paint.
 */
export function DeferredOneTap(props: DeferredOneTapProps) {
	const [shouldMount, setShouldMount] = useState(false);

	useEffect(() => {
		const handle = scheduleIdle(() => setShouldMount(true));
		return () => cancelIdle(handle);
	}, []);

	if (!shouldMount) return null;
	return <OneTapMount {...props} />;
}

function OneTapMount({ clientId, ssoUrl, ssoSession }: DeferredOneTapProps) {
	useOneTap({ clientId, ssoUrl, ssoSession });
	return null;
}

type IdleWindow = Window & {
	requestIdleCallback?: (
		cb: () => void,
		options?: { timeout: number },
	) => number;
	cancelIdleCallback?: (handle: number) => void;
};

// `requestIdleCallback` is missing in Safari, so we always need a
// `setTimeout` fallback. The same value also acts as the hard upper
// bound on browsers that do support it: without `timeout`,
// `requestIdleCallback` can sit indefinitely under sustained main-
// thread work — One Tap would then never initialize and signed-out
// users would never see the prompt.
const IDLE_TIMEOUT_MS = 3000;

function scheduleIdle(cb: () => void): number {
	if (typeof window === "undefined") return 0;
	const w = window as IdleWindow;
	if (w.requestIdleCallback)
		return w.requestIdleCallback(cb, { timeout: IDLE_TIMEOUT_MS });
	return window.setTimeout(cb, IDLE_TIMEOUT_MS);
}

function cancelIdle(handle: number): void {
	if (typeof window === "undefined") return;
	const w = window as IdleWindow;
	if (w.cancelIdleCallback) {
		w.cancelIdleCallback(handle);
		return;
	}
	window.clearTimeout(handle);
}
